import { memo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Drawer,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';

import FormNumberField from 'components/ui/form/FormNumberField';
import FormCurrencyField from 'components/ui/form/FormCurrencyField';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import CalculatorForPrint from './CalculatorForPrint';
import { useReactToPrint } from 'react-to-print';

const validationSchema = yup.object({
  amount: yup.number().required('calculation.validation.amount'),
  percent: yup
    .number()
    .min(1, 'calculation.validation.min.percent')
    .max(100, 'calculation.validation.max.percent')
    .required('calculation.validation.percent'),
  months: yup
    .number()
    .min(6, 'calculation.validation.min.months')
    .max(360, 'calculation.validation.max.months')
    .required('calculation.validation.months'),
});

const CalculatorDrawer = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const [calculations, setCalculations] = useState({});

  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef?.current,
    documentTitle: `calculation`,
    pageStyle: `
          @media print {
            @page { size: portrait; margin: 0mm; }
          }
          body {
            -webkit-print-color-adjust: exact;
          }
        `,
  });

  const formik = useFormik({
    initialValues: {
      amount: '',
      months: '120',
      percent: '17.5',
      type: 'annuitet',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const calculations = calculateMortgage(values);
      setCalculations(calculations);
    },
  });

  function calculateMortgage({ amount, percent, months, type }) {
    const monthlyRate = percent / 100 / 12;
    let schedule = [];
    let totalPayment = 0;
    let overpayment = 0;

    if (type === 'annuitet') {
      const annuityFactor =
        (monthlyRate * Math.pow(1 + monthlyRate, months)) /
        (Math.pow(1 + monthlyRate, months) - 1);
      const monthlyPayment = amount * annuityFactor;

      for (let i = 0; i < months; i++) {
        const interest = amount * monthlyRate;
        const principal = monthlyPayment - interest;
        amount -= principal;

        schedule.push({
          month: i + 1,
          payment: monthlyPayment,
          interest,
          principal,
          remaining: amount,
        });

        totalPayment += monthlyPayment;
      }
    }

    if (type === 'differensial') {
      const principalPart = amount / months;

      for (let i = 0; i < months; i++) {
        const interest = amount * monthlyRate;
        const monthlyPayment = principalPart + interest;
        amount -= principalPart;

        schedule.push({
          month: i + 1,
          payment: monthlyPayment,
          interest,
          principal: principalPart,
          remaining: amount,
        });

        totalPayment += monthlyPayment;
      }
    }

    overpayment =
      totalPayment - schedule.reduce((sum, item) => sum + item.principal, 0);

    return {
      schedule,
      totalPayment: totalPayment.toFixed(2),
      overpayment: overpayment.toFixed(2),
    };
  }

  return (
    <Drawer anchor='right' open={open} onClose={() => setOpen(false)}>
      <div className='border-b flex items-center justify-between pr-16'>
        <div className='drawer-title p-2 pl-6 font-semibold text-lg'>
          <span className='mr-4'>{t('common.table.creditCalc')}</span>
        </div>
        <Button
          startIcon={<i className='bi bi-printer text-lg mr-1' />}
          variant='base'
          size='small'
          className='mr-4'
          onClick={handlePrint}
          disabled={!calculations?.overpayment}
        >
          {t('common.button.print')}
        </Button>
        <div className='close-btn-wrapper'>
          <IconButton
            variant='onlyIcon'
            color='primary'
            onClick={() => setOpen(false)}
          >
            <i className='bi bi-x' />
          </IconButton>
        </div>
      </div>
      <div className='calls-history-drawer-wrapper'>
        <div className='drawer-body hidden'>
          <div ref={printRef}>
            <CalculatorForPrint formik={formik} calculations={calculations} />
          </div>
        </div>
        <div className='drawer-body'>
          <form onSubmit={formik.handleSubmit}>
            <Grid
              container
              spacing={{ xs: 2, sm: 3, lg: 3 }}
              rowSpacing={1}
              columns={{ xs: 12, sm: 12, lg: 12 }}
            >
              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormSimpleSelectField
                  delay={0}
                  label={t('common.fields.creditCalculationType')}
                  fieldName='type'
                  options={[
                    { name: 'Annuitet (teng to‘lovli kredit)', id: 'annuitet' },
                    {
                      name: 'Differensial (kamayib boruvchi to‘lovli kredit)',
                      id: 'differensial',
                    },
                  ]}
                  formik={formik}
                />
              </Grid>
              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormCurrencyField
                  delay={0.1}
                  label={t('common.filter.totalPrice')}
                  fieldName='amount'
                  formik={formik}
                />
              </Grid>

              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormNumberField
                  delay={0.2}
                  label={t('common.fields.month')}
                  fieldName='months'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormCurrencyField
                  delay={0.3}
                  label={t('common.fields.percent')}
                  fieldName='percent'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} lg={12} sm={12} xs={12}>
                <Button
                  type='submit'
                  fullWidth
                  variant='contained'
                  color='info'
                >
                  {t('common.button.calc')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </div>
        <div className='mt-2 p-2 bg-gray-100 flex justify-between rounded-lg gap-2'>
          <div className='bg-gray-50 p-2 rounded-md grow'>
            <div>{t('payment.add.contract.totalSum')}</div>
            <span className='text-green-600'>
              <CurrencyFormat value={calculations?.totalPayment} suffix='' />
            </span>
          </div>
          <div className='bg-gray-50 p-2 rounded-md grow'>
            <div>{t('common.fields.overpayment')}</div>
            <span className='text-green-600'>
              <CurrencyFormat value={calculations?.overpayment} suffix='' />
            </span>
          </div>
        </div>
        <Accordion
          sx={{
            boxShadow: 'none',
          }}
          className={`dashboard-filter-accordion !my-2`}
        >
          <AccordionSummary
            aria-controls={`1-content`}
            id={`1-header`}
            data-text={t('shaxmatka.homeDetail.more')}
            sx={{ border: '1px solid' }}
            expandIcon={<i className='bi bi-chevron-right expand-icon' />}
            className='!border !border-gray-300 hover:!border-gray-500 !px-2 !shadow-none'
          >
            <i className='bi bi-table mr-2' />
            <span className='accordion-title'>
              {t('shaxmatka.homeDetail.more')}
            </span>
          </AccordionSummary>
          <AccordionDetails className='dashboard-filter-accordion-details !px-0'>
            <div className='calls-history-tab-item h-[calc(100vh-400px)]'>
              <TableContainer
                className='tab-table-container !shadow-none !base-border h-full'
                component='div'
              >
                <Table stickyHeader component='div'>
                  <TableHead component='div'>
                    <TableRow component='div'>
                      <TableCell component='div'>
                        {t('common.table.month')}
                      </TableCell>
                      <TableCell component='div'>
                        {t('shaxmatka.homeDetail.monthlyPay')}
                      </TableCell>
                      <TableCell component='div'>
                        {t('common.table.principal')}
                      </TableCell>
                      <TableCell component='div'>
                        {t('common.table.interest')}
                      </TableCell>
                      <TableCell component='div'>
                        {t('shaxmatka.moreHomeDetails.remainingPayment')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  {calculations?.schedule?.length > 0 ? (
                    <TableBody
                      className='overflow-hidden !bg-white'
                      component='div'
                    >
                      {calculations?.schedule?.map((row, rowIndex) => {
                        return (
                          <TableRow hover tabIndex={-1} key={'row-' + rowIndex}>
                            <TableCell size='small' component='div'>
                              {row?.month}
                            </TableCell>
                            <TableCell size='small' component='div'>
                              <CurrencyFormat
                                value={Number(row?.payment)}
                                decimalScale={2}
                                suffix=''
                              />
                            </TableCell>
                            <TableCell size='small' component='div'>
                              <CurrencyFormat
                                value={Number(row?.principal)}
                                decimalScale={2}
                                suffix=''
                              />
                            </TableCell>
                            <TableCell size='small' component='div'>
                              <CurrencyFormat
                                value={Number(row?.interest)}
                                decimalScale={2}
                                suffix=''
                              />
                            </TableCell>
                            <TableCell size='small' component='div'>
                              <CurrencyFormat
                                value={Number(row?.remaining)}
                                decimalScale={2}
                                suffix=''
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  ) : (
                    <TableBody component='div'>
                      <TableRow component='div'>
                        <TableCell colSpan={6}>
                          <span className='text-gray-400 flex items-center justify-center'>
                            <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                            {t('common.global.noDataFound')}
                          </span>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </Drawer>
  );
};
export default memo(CalculatorDrawer);
